import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDataCache } from '../contexts/DataCacheContext'
import { resolveSalePricing } from '../supabase-client'
import { getTodayISOString, toLocalDateString, parseDateString } from '../utils/dateUtils.js'
import CustomerForm from './CustomerForm'
import RecurringOrderOptions from './RecurringOrderOptions'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog'
import { DatePicker } from '@/components/ui/date-picker'
import { Checkbox } from '@/components/ui/checkbox'
import Combobox from '@/components/ui/combobox'
import { X, Plus, Trash2, Pencil } from 'lucide-react'
import { cn } from '@/lib/utils'

// Fruiting Blocks builder retired — substrate blocks now sold via Growing
// Materials. Flip to true to temporarily restore the legacy block builder.
const SHOW_FRUITING_BLOCKS_BUILDER = false

const formatDate = (date) => {
  const dateObject = date instanceof Date ? date : parseDateString(date)
  return dateObject.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: '2-digit'
  })
}

export default function CreateSaleModal({
  isOpen,
  onClose,
  onSave,
  onDelete = null,
  editingSale = null,
  savedFormData = null,
  onFormDataChange = null,
  onRequestReopen = null,
  onClearFormData = null
}) {
  const navigate = useNavigate()
  const { getCustomers, getProducts, getProductTypes,
          getPriceTiers, getBlockFormats, getBlockPrices,
          getGrowingMaterials, getGrowingMaterialPrices,
          snapshot } = useDataCache()

  const goToGrowingMaterialsCatalogue = useCallback(() => {
    onClose()
    navigate('/settings/products?tab=growingMaterials')
  }, [navigate, onClose])

  // --- Render-time initialisation from snapshot (no useEffect cascade) ---
  // Parent uses key={editingSale?.id || 'new'} to force remount on mode switch.
  const cacheWarm = !!(snapshot.customers && snapshot.products && snapshot.productTypes &&
    snapshot.priceTiers && snapshot.blockFormats && snapshot.blockPrices &&
    snapshot.growingMaterials && snapshot.growingMaterialPrices)

  const [customers, setCustomers] = useState(() => snapshot.customers || [])
  const [products, setProducts] = useState(() => snapshot.products || [])
  const [productTypes, setProductTypes] = useState(() => snapshot.productTypes || [])
  const [priceTiers, setPriceTiers] = useState(() => snapshot.priceTiers || [])
  const [pricingLoading, setPricingLoading] = useState(false)

  // Seed form fields from editingSale/savedFormData at mount — zero-delay init
  const [selectedCustomer, setSelectedCustomer] = useState(() =>
    editingSale?.customer_id || savedFormData?.selectedCustomer || ''
  )
  const [selectedBranch, setSelectedBranch] = useState(() =>
    editingSale?.branch_id || savedFormData?.selectedBranch || ''
  )
  const [customerBranches, setCustomerBranches] = useState(() => {
    const custId = editingSale?.customer_id || savedFormData?.selectedCustomer
    if (!custId || !snapshot.customers) return []
    const customer = snapshot.customers.find(c => c.id === custId)
    return (customer?.branches || [])
      .filter(b => b.branch_type !== 'company')
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  })
  const [selectedDate, setSelectedDate] = useState(() =>
    editingSale?.date || savedFormData?.selectedDate || getTodayISOString()
  )
  const [consignmentNo, setConsignmentNo] = useState(() =>
    editingSale?.consignment_no || savedFormData?.consignmentNo || ''
  )
  const [loading, setLoading] = useState(false)
  const [showQuickCustomerForm, setShowQuickCustomerForm] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [pendingDelete, setPendingDelete] = useState(false)
  const [editingQuantities, setEditingQuantities] = useState({})

  // Recurring order state (US-RECURRING-ORDERS)
  const [isRecurring, setIsRecurring] = useState(false)
  const [recurringDays, setRecurringDays] = useState([])
  const [recurringEndType, setRecurringEndType] = useState('indefinite')
  const [recurringEndDate, setRecurringEndDate] = useState(null)
  const [recurringOccurrences, setRecurringOccurrences] = useState(null)

  // Mark as initialised at mount if cache was warm — prevents cold-cache effect from running
  const isInitializedRef = useRef(cacheWarm)
  const isMountedRef = useRef(false)
  const pricingDebounceRef = useRef(null)
  const scrollContainerRef = useRef(null)
  const saleLineIdRef = useRef(0)

  // Compute initial sale items at mount (synchronous, no effect)
  const [saleItems, setSaleItems] = useState(() => {
    const prods = snapshot.products || []
    const types = snapshot.productTypes || []
    const tiers = snapshot.priceTiers || []

    // In edit mode, always use editingSale data — never the new-sale draft from localStorage.
    if (!editingSale && savedFormData?.saleItems) {
      const maxId = savedFormData.saleItems.reduce((max, item) => Math.max(max, item._lineId || 0), 0)
      saleLineIdRef.current = maxId
      return savedFormData.saleItems
    }

    if (editingSale && prods.length > 0 && types.length > 0) {
      const defaultProductType = types.find(pt => pt.name === 'A Class') || types[0]
      const freshEditItems = (editingSale.items || []).filter(i => !i.block_format_id && !i.growing_material_id)
      const rows = []
      prods.forEach(product => {
        const matchingItems = freshEditItems.filter(item => item.product_id === product.id)
        if (matchingItems.length > 0) {
          matchingItems.forEach((existingItem, idx) => {
            const productType = types.find(pt => pt.id === existingItem.product_type_id) || types.find(pt => pt.name === existingItem.productType) || defaultProductType
            const discount = existingItem.discount_percent || 0
            const basePrice = existingItem.price || 0
            const effectivePrice = basePrice * (1 - discount / 100)
            rows.push({
              _lineId: ++saleLineIdRef.current,
              _isPrimary: idx === 0,
              id: product.id, product_id: product.id,
              productName: product.strain, strain: product.strain,
              product_type_id: productType.id,
              quantity: existingItem.quantity || 0,
              catalogue_price: basePrice,
              price: effectivePrice,
              subtotal: (existingItem.quantity || 0) * effectivePrice,
              volume_discount_percent: discount
            })
          })
        } else {
          rows.push({
            _lineId: ++saleLineIdRef.current,
            _isPrimary: true,
            id: product.id, product_id: product.id,
            productName: product.strain, strain: product.strain,
            product_type_id: defaultProductType?.id || null,
            quantity: 0, catalogue_price: product.base_price_per_kg || 0,
            price: product.base_price_per_kg || 0, subtotal: 0
          })
        }
      })
      return rows
    }

    if (prods.length > 0 && types.length > 0) {
      const defaultProductType = types.find(pt => pt.name === 'A Class') || types[0]
      const retailTier = tiers.find(t => t.name === 'retail')
      return prods.map(product => ({
        _lineId: ++saleLineIdRef.current,
        _isPrimary: true,
        id: product.id, product_id: product.id,
        productName: product.strain, strain: product.strain,
        product_type_id: defaultProductType?.id || null,
        quantity: 0, catalogue_price: product.base_price_per_kg || 0,
        price: product.base_price_per_kg || 0, subtotal: 0,
        applied_price_tier_id: retailTier?.id || null
      }))
    }

    return []
  })

  // --- Block items for fruiting blocks ---
  const [blockFormats, setBlockFormats] = useState(() => snapshot.blockFormats?.filter(f => f.active) || [])
  const blockLineIdRef = useRef(0)
  const [blockDraft, setBlockDraft] = useState({ product_id: '', block_format_id: '', quantity: '' })
  const [blockEligible, setBlockEligible] = useState(() => {
    const custId = editingSale?.customer_id || savedFormData?.selectedCustomer
    if (!custId || !snapshot.customers) return false
    const customer = snapshot.customers.find(c => c.id === custId)
    return !!customer?.fruiting_blocks_eligible
  })

  const blockPriceMapRef = useRef(() => {
    const map = new Map()
    for (const row of (snapshot.blockPrices || [])) {
      map.set(`${row.product_id}-${row.price_tier_id}`, parseFloat(row.price_per_kg))
    }
    return map
  })
  if (typeof blockPriceMapRef.current === 'function') {
    blockPriceMapRef.current = blockPriceMapRef.current()
  }

  const [blockItems, setBlockItems] = useState(() => {
    if (!editingSale && savedFormData?.blockItems) {
      // Restore _lineId counter from persisted items
      const maxId = savedFormData.blockItems.reduce((max, item) => Math.max(max, item._lineId || 0), 0)
      blockLineIdRef.current = maxId
      return savedFormData.blockItems
    }

    // Edit mode: populate only actual block sale items (not one-per-product)
    if (editingSale) {
      const blockEditItems = (editingSale.items || []).filter(i => i.block_format_id)
      if (blockEditItems.length > 0) {
        const prods = snapshot.products || []
        const formats = (snapshot.blockFormats || []).filter(f => f.active)

        return blockEditItems.map(existing => {
          const product = prods.find(p => p.id === existing.product_id)
          const format = formats.find(f => f.id === existing.block_format_id) || existing.block_format
          const formatWeight = format ? parseFloat(format.weight_kg) : 0
          const lineId = ++blockLineIdRef.current
          return {
            _lineId: lineId,
            product_id: existing.product_id,
            strain: product?.strain || 'Unknown',
            block_format_id: existing.block_format_id,
            format_weight_kg: formatWeight,
            format_name: format?.name || '',
            quantity: existing.quantity || 0,
            price_per_kg: 0, // will be repriced when customer pricing runs
            cost_per_block: existing.price || 0,
            subtotal: (existing.quantity || 0) * (existing.price || 0),
            applied_price_tier_id: existing.applied_price_tier_id || null
          }
        })
      }
    }

    return []
  })

  // --- Growing materials (substrate logs, spawn, straw pellets) ---
  const [growingMaterials, setGrowingMaterials] = useState(() => snapshot.growingMaterials?.filter(m => m.active) || [])
  const gmLineIdRef = useRef(0)
  const [gmDraft, setGmDraft] = useState({ growing_material_id: '', quantity: '' })
  const [gmEligible, setGmEligible] = useState(() => {
    const custId = editingSale?.customer_id || savedFormData?.selectedCustomer
    if (!custId || !snapshot.customers) return false
    const customer = snapshot.customers.find(c => c.id === custId)
    return !!customer?.growing_materials_eligible
  })

  const gmPriceMapRef = useRef(() => {
    const map = new Map()
    for (const row of (snapshot.growingMaterialPrices || [])) {
      map.set(`${row.growing_material_id}-${row.price_tier_id}`, parseFloat(row.unit_price))
    }
    return map
  })
  if (typeof gmPriceMapRef.current === 'function') {
    gmPriceMapRef.current = gmPriceMapRef.current()
  }

  const [gmItems, setGmItems] = useState(() => {
    if (savedFormData?.gmItems) {
      const maxId = savedFormData.gmItems.reduce((max, item) => Math.max(max, item._lineId || 0), 0)
      gmLineIdRef.current = maxId
      return savedFormData.gmItems
    }

    if (editingSale) {
      const gmEditItems = (editingSale.items || []).filter(i => i.growing_material_id)
      if (gmEditItems.length > 0) {
        const mats = (snapshot.growingMaterials || [])
        return gmEditItems.map(existing => {
          const material = mats.find(m => m.id === existing.growing_material_id) || existing.growing_material
          return {
            _lineId: ++gmLineIdRef.current,
            growing_material_id: existing.growing_material_id,
            name: material?.name || 'Unknown',
            unit_label: material?.unit_label || 'unit',
            quantity: existing.quantity || 0,
            unit_price: existing.price || 0,
            subtotal: (existing.quantity || 0) * (existing.price || 0),
            applied_price_tier_id: existing.applied_price_tier_id || null
          }
        })
      }
    }

    return []
  })

  const roundQuantity = (value) => {
    const num = parseFloat(value)
    if (isNaN(num) || num < 0) return 0
    return Math.round(num * 200) / 200 // rounds to nearest 0.005
  }

  // Cold-cache fallback — only fires if snapshot was empty at mount
  useEffect(() => {
    if (!isOpen || cacheWarm) return

    const loadColdCache = async () => {
      setLoading(true)
      try {
        const [c, p, pt, tier, bf, bp, gm, gmp] = await Promise.all([
          getCustomers(), getProducts(), getProductTypes(),
          getPriceTiers(), getBlockFormats(), getBlockPrices(),
          getGrowingMaterials(), getGrowingMaterialPrices()
        ])
        setCustomers(c)
        setProducts(p)
        setProductTypes(pt)
        setPriceTiers(tier)

        // Block price map (still needed for handleAddBlockLine immediate display)
        const bpMap = new Map()
        for (const row of (bp || [])) {
          bpMap.set(`${row.product_id}-${row.price_tier_id}`, parseFloat(row.price_per_kg))
        }
        blockPriceMapRef.current = bpMap
        setBlockFormats((bf || []).filter(f => f.active))

        // Growing material price map (immediate display in handleAddGmLine)
        const gmMap = new Map()
        for (const row of (gmp || [])) {
          gmMap.set(`${row.growing_material_id}-${row.price_tier_id}`, parseFloat(row.unit_price))
        }
        gmPriceMapRef.current = gmMap
        setGrowingMaterials((gm || []).filter(m => m.active))

        const defaultPT = pt.find(t => t.name === 'A Class') || pt[0]

        if (editingSale) {
          // Edit mode on cold cache — populate form from editingSale + freshly loaded data
          setSelectedCustomer(editingSale.customer_id || '')
          const cust = c.find(cc => cc.id === editingSale.customer_id)
          const branches = (cust?.branches || [])
            .filter(b => b.branch_type !== 'company')
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
          setCustomerBranches(branches)
          setSelectedBranch(editingSale.branch_id || '')
          setSelectedDate(editingSale.date || getTodayISOString())
          setConsignmentNo(editingSale.consignment_no || '')

          const freshEditItems = (editingSale.items || []).filter(i => !i.block_format_id && !i.growing_material_id)
          const blockEditItems = (editingSale.items || []).filter(i => i.block_format_id)

          const coldEditRows = []
          p.forEach(product => {
            const matchingItems = freshEditItems.filter(item => item.product_id === product.id)
            if (matchingItems.length > 0) {
              matchingItems.forEach((existingItem, idx) => {
                const productType = pt.find(t => t.id === existingItem.product_type_id) || pt.find(t => t.name === existingItem.productType) || defaultPT
                coldEditRows.push({
                  _lineId: ++saleLineIdRef.current,
                  _isPrimary: idx === 0,
                  id: product.id, product_id: product.id,
                  productName: product.strain, strain: product.strain,
                  product_type_id: productType.id,
                  quantity: existingItem.quantity || 0,
                  price: existingItem.price || 0,
                  subtotal: existingItem.total || 0
                })
              })
            } else {
              coldEditRows.push({
                _lineId: ++saleLineIdRef.current,
                _isPrimary: true,
                id: product.id, product_id: product.id,
                productName: product.strain, strain: product.strain,
                product_type_id: defaultPT?.id || null,
                quantity: 0, price: product.base_price_per_kg || 0, subtotal: 0
              })
            }
          })
          setSaleItems(coldEditRows)

          // Initialise block items for edit mode (cold cache)
          if (blockEditItems.length > 0) {
            const activeBf = (bf || []).filter(f => f.active)
            const blockRows = blockEditItems.map(existing => {
              const product = p.find(pr => pr.id === existing.product_id)
              const fmt = activeBf.find(f => f.id === existing.block_format_id) || existing.block_format
              const fmtWeight = fmt ? parseFloat(fmt.weight_kg) : 0
              return {
                _lineId: ++blockLineIdRef.current,
                product_id: existing.product_id, strain: product?.strain || '',
                block_format_id: existing.block_format_id,
                format_weight_kg: fmtWeight, format_name: fmt?.name || '',
                quantity: existing.quantity || 0, price_per_kg: 0,
                cost_per_block: existing.price || 0,
                subtotal: (existing.quantity || 0) * (existing.price || 0),
                applied_price_tier_id: existing.applied_price_tier_id || null
              }
            })
            setBlockItems(blockRows)
          }

          // Initialise growing-material items for edit mode (cold cache)
          const gmEditItems = (editingSale.items || []).filter(i => i.growing_material_id)
          if (gmEditItems.length > 0) {
            const activeGm = (gm || []).filter(m => m.active)
            setGmItems(gmEditItems.map(existing => {
              const material = activeGm.find(m => m.id === existing.growing_material_id) || existing.growing_material
              return {
                _lineId: ++gmLineIdRef.current,
                growing_material_id: existing.growing_material_id,
                name: material?.name || '',
                unit_label: material?.unit_label || 'unit',
                quantity: existing.quantity || 0,
                unit_price: existing.price || 0,
                subtotal: (existing.quantity || 0) * (existing.price || 0),
                applied_price_tier_id: existing.applied_price_tier_id || null
              }
            }))
          }

          // Fetch server-side pricing for the edit sale
          if (editingSale.customer_id) {
            fetchPricing(editingSale.customer_id, coldEditRows)
          }
        } else if (!savedFormData) {
          const retailTier = tier.find(t => t.name === 'retail')
          saleLineIdRef.current = 0
          setSaleItems(p.map(product => ({
            _lineId: ++saleLineIdRef.current,
            _isPrimary: true,
            id: product.id, product_id: product.id,
            productName: product.strain, strain: product.strain,
            product_type_id: defaultPT?.id || null,
            quantity: 0, price: product.base_price_per_kg || 0, subtotal: 0,
            applied_price_tier_id: retailTier?.id || null
          })))
        } else if (savedFormData?.selectedCustomer) {
          // Restore savedFormData and trigger server pricing
          fetchPricing(savedFormData.selectedCustomer)
        }
      } catch (error) {
        console.error('Error loading initial data:', error)
      } finally {
        setLoading(false)
      }
    }

    loadColdCache()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Always refresh customers when modal opens (cache may be stale from other pages)
  useEffect(() => {
    if (!isOpen || !cacheWarm) return
    getCustomers(true).then(c => setCustomers(c)).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Save form data when it changes (for persistence) - debounced
  useEffect(() => {
    // Skip first render to avoid saving incomplete form on mount
    if (!isMountedRef.current) {
      isMountedRef.current = true
      return
    }

    if (isOpen && !editingSale && onFormDataChange && selectedCustomer) {
      const timeoutId = setTimeout(() => {
        const formData = {
          selectedCustomer,
          selectedBranch,
          selectedDate,
          consignmentNo,
          saleItems,
          blockItems,
          gmItems
        }
        onFormDataChange(formData)
      }, 1500)

      return () => clearTimeout(timeoutId)
    }
  }, [selectedCustomer, selectedBranch, selectedDate, consignmentNo, saleItems, blockItems, gmItems, isOpen, editingSale, onFormDataChange])

  // Reset mounted flag and clear debounce when modal closes
  useEffect(() => {
    if (!isOpen) {
      isMountedRef.current = false
      if (pricingDebounceRef.current) {
        clearTimeout(pricingDebounceRef.current)
      }
    }
  }, [isOpen])

  const loadCustomerBranches = useCallback((customerId, preserveSelection = false, existingBranchId = null) => {
    if (!customerId) {
      setCustomerBranches([])
      setSelectedBranch('')
      return
    }

    // Derive branches from cached customers — no network call needed
    const customer = customers.find(c => c.id === customerId)
    const branches = (customer?.branches || [])
      .filter(b => b.branch_type !== 'company')
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))

    setCustomerBranches(branches)

    if (!preserveSelection) {
      if (branches.length === 1) {
        setSelectedBranch(branches[0].id)
      } else {
        setSelectedBranch('')
      }
    } else if (existingBranchId) {
      const branchExists = branches.some(b => b.id === existingBranchId)
      if (branchExists) {
        setSelectedBranch(existingBranchId)
      } else if (branches.length === 1) {
        setSelectedBranch(branches[0].id)
      } else {
        setSelectedBranch('')
      }
    } else {
      if (branches.length === 1) {
        setSelectedBranch(branches[0].id)
      }
    }
  }, [customers])

  /**
   * Unified Pricing Engine — resolve pricing for an entire sale via server-side Edge Function.
   * Replaces all client-side getApplicableTier / getVolumeDiscount / getCataloguePrice logic.
   * Uses function parameters when provided (avoids stale closure from setState not yet flushing).
   */
  // Refs to always read latest state in debounced/async callbacks (avoids stale closures)
  const saleItemsRef = useRef(saleItems)
  saleItemsRef.current = saleItems
  const blockItemsRef = useRef(blockItems)
  blockItemsRef.current = blockItems

  const gmItemsRef = useRef(gmItems)
  gmItemsRef.current = gmItems

  const fetchPricing = useCallback(async (customerId, currentItems, currentBlockItems) => {
    if (!customerId) return

    // Only price items that have a product_type_id (valid items)
    const itemsToPrice = (currentItems || saleItemsRef.current).filter(i => i.product_type_id)
    if (itemsToPrice.length === 0) return

    setPricingLoading(true)
    try {
      const result = await resolveSalePricing({
        customerId,
        items: itemsToPrice.map(i => ({
          product_id: i.product_id,
          product_type_id: i.product_type_id,
          quantity: i.quantity
        })),
        blockItems: (currentBlockItems || blockItemsRef.current)?.filter(i => i.quantity > 0).map(i => ({
          product_id: i.product_id,
          block_format_id: i.block_format_id,
          quantity: i.quantity
        })),
        growingMaterialItems: gmItemsRef.current?.filter(i => i.quantity > 0).map(i => ({
          growing_material_id: i.growing_material_id,
          quantity: i.quantity
        })),
        date: selectedDate
      })

      // Merge server prices into sale items state
      setSaleItems(prev => prev.map(item => {
        const serverPrice = result.items?.find(r =>
          r.product_id === item.product_id && r.product_type_id === item.product_type_id
        )
        if (!serverPrice || serverPrice.error) return item
        return {
          ...item,
          catalogue_price: serverPrice.catalogue_price,
          price: serverPrice.final_price,
          subtotal: item.quantity * serverPrice.final_price,
          applied_price_tier_id: serverPrice.applied_price_tier_id,
          volume_discount_percent: serverPrice.discount_percent,
          promotion_name: serverPrice.discount_note
        }
      }))

      // Merge block prices if present
      if (result.block_items?.length > 0) {
        setBlockItems(prev => prev.map(item => {
          const serverPrice = result.block_items.find(r =>
            r.product_id === item.product_id && r.block_format_id === item.block_format_id
          )
          if (!serverPrice || serverPrice.error) return item
          return {
            ...item,
            price_per_kg: serverPrice.price_per_kg,
            cost_per_block: serverPrice.cost_per_block,
            subtotal: item.quantity * serverPrice.cost_per_block,
            applied_price_tier_id: serverPrice.applied_price_tier_id
          }
        }))
      }

      // Merge growing-material prices if present
      if (result.growing_material_items?.length > 0) {
        setGmItems(prev => prev.map(item => {
          const serverPrice = result.growing_material_items.find(r =>
            r.growing_material_id === item.growing_material_id
          )
          if (!serverPrice || serverPrice.error) return item
          return {
            ...item,
            unit_price: serverPrice.unit_price,
            subtotal: item.quantity * serverPrice.unit_price,
            applied_price_tier_id: serverPrice.applied_price_tier_id
          }
        }))
      }
    } catch (err) {
      console.error('[CreateSaleModal] Pricing resolution failed:', err)
    } finally {
      setPricingLoading(false)
    }
  }, [selectedDate])

  const triggerPricingUpdate = useCallback((customerId, items, blockItemsOverride) => {
    if (pricingDebounceRef.current) clearTimeout(pricingDebounceRef.current)
    pricingDebounceRef.current = setTimeout(() => {
      fetchPricing(customerId, items, blockItemsOverride)
    }, 300)
  }, [fetchPricing])


  // Cold-cache form init — only runs when data arrives late (not at mount)
  // Warm-cache path: all state is seeded via useState lazy initialisers above
  useEffect(() => {
    if (!isOpen || isInitializedRef.current) return
    if (products.length === 0 || productTypes.length === 0) return

    // If we reach here, data arrived via the cold-cache effect above
    isInitializedRef.current = true

    if (editingSale) {
      setSelectedCustomer(editingSale.customer_id || '')
      setSelectedBranch(editingSale.branch_id || '')
      setSelectedDate(editingSale.date || getTodayISOString())
      setConsignmentNo(editingSale.consignment_no || '')
      if (editingSale.customer_id) {
        loadCustomerBranches(editingSale.customer_id, true, editingSale.branch_id)
      }
      const defaultPT = productTypes.find(pt => pt.name === 'A Class') || productTypes[0]
      const freshEditItems = (editingSale.items || []).filter(i => !i.block_format_id && !i.growing_material_id)
      const effectRows = []
      products.forEach(product => {
        const matchingItems = freshEditItems.filter(i => i.product_id === product.id)
        if (matchingItems.length > 0) {
          matchingItems.forEach((existing, idx) => {
            const pt = productTypes.find(t => t.id === existing.product_type_id) || productTypes.find(t => t.name === existing.productType) || defaultPT
            const discount = existing.discount_percent || 0
            const basePrice = existing.price || 0
            const effectivePrice = basePrice * (1 - discount / 100)
            effectRows.push({ _lineId: ++saleLineIdRef.current, _isPrimary: idx === 0,
              id: product.id, product_id: product.id, productName: product.strain, strain: product.strain,
              product_type_id: pt.id, quantity: existing.quantity || 0, catalogue_price: basePrice,
              price: effectivePrice, subtotal: (existing.quantity || 0) * effectivePrice,
              volume_discount_percent: discount })
          })
        } else {
          effectRows.push({ _lineId: ++saleLineIdRef.current, _isPrimary: true,
            id: product.id, product_id: product.id, productName: product.strain, strain: product.strain,
            product_type_id: defaultPT?.id || null, quantity: 0, catalogue_price: product.base_price_per_kg || 0,
            price: product.base_price_per_kg || 0, subtotal: 0 })
        }
      })
      setSaleItems(effectRows)
      // Recalculate all prices via server-side pricing engine
      if (editingSale.customer_id) {
        fetchPricing(editingSale.customer_id, effectRows)
        // Init block items from existing block sale items
        const blockEditItems = (editingSale.items || []).filter(i => i.block_format_id)
        if (blockEditItems.length > 0) {
          initBlockItemsForCustomer(editingSale.customer_id, blockEditItems.map(i => ({
            product_id: i.product_id, strain: products.find(p => p.id === i.product_id)?.strain || '',
            block_format_id: i.block_format_id,
            format_weight_kg: i.block_format ? parseFloat(i.block_format.weight_kg) : 0,
            format_name: i.block_format?.name || '',
            quantity: i.quantity || 0, price_per_kg: 0, cost_per_block: i.price || 0,
            subtotal: (i.quantity || 0) * (i.price || 0), applied_price_tier_id: i.applied_price_tier_id || null
          })))
        } else {
          initBlockItemsForCustomer(editingSale.customer_id)
        }
        // Init growing-material items from existing gm sale items
        const gmEditItems = (editingSale.items || []).filter(i => i.growing_material_id)
        if (gmEditItems.length > 0) {
          initGmItemsForCustomer(editingSale.customer_id, gmEditItems.map(i => ({
            growing_material_id: i.growing_material_id,
            name: i.growing_material?.name || '',
            unit_label: i.growing_material?.unit_label || 'unit',
            quantity: i.quantity || 0, unit_price: i.price || 0,
            subtotal: (i.quantity || 0) * (i.price || 0), applied_price_tier_id: i.applied_price_tier_id || null
          })))
        } else {
          initGmItemsForCustomer(editingSale.customer_id)
        }
      }
    } else if (savedFormData) {
      setSelectedCustomer(savedFormData.selectedCustomer || '')
      setSelectedBranch(savedFormData.selectedBranch || '')
      setSelectedDate(savedFormData.selectedDate || getTodayISOString())
      setConsignmentNo(savedFormData.consignmentNo || '')
      setSaleItems(savedFormData.saleItems || [])
      setBlockItems(savedFormData.blockItems || [])
      setGmItems(savedFormData.gmItems || [])
      if (savedFormData.selectedCustomer) {
        loadCustomerBranches(savedFormData.selectedCustomer, true, savedFormData.selectedBranch)
        const cust = customers.find(c => c.id === savedFormData.selectedCustomer)
        setBlockEligible(!!cust?.fruiting_blocks_eligible)
        setGmEligible(!!cust?.growing_materials_eligible)
        fetchPricing(savedFormData.selectedCustomer)
      }
    }

    return () => { isInitializedRef.current = false }
  }, [isOpen, products, productTypes]) // eslint-disable-line react-hooks/exhaustive-deps

  // Recalculate prices for existing sales via server-side pricing engine
  // Handles warm-cache path where the cold-cache effect is skipped
  const hasRecalculatedRef = useRef(false)
  useEffect(() => {
    if (!isOpen) {
      hasRecalculatedRef.current = false
      return
    }
    if (hasRecalculatedRef.current) return
    if (!editingSale?.customer_id) return
    if (saleItems.length === 0) return
    hasRecalculatedRef.current = true
    fetchPricing(editingSale.customer_id)
  }, [isOpen, editingSale, saleItems.length, fetchPricing])

  /**
   * Initialise or update block items for a customer.
   * Only shows block rows if customer is fruiting_blocks_eligible.
   */
  const initBlockItemsForCustomer = useCallback((customerId, existingBlockItems = null) => {
    if (!customerId) { setBlockEligible(false); setBlockItems([]); return }

    const customer = customers.find(c => c.id === customerId)
    if (!customer?.fruiting_blocks_eligible) { setBlockEligible(false); setBlockItems([]); return }

    setBlockEligible(true)

    const defaultBranch = customer.branches?.[0]
    const tierId = defaultBranch?.customer_type?.default_price_tier_id

    if (existingBlockItems && existingBlockItems.length > 0) {
      // Edit mode — re-price existing block items with current tier
      setBlockItems(existingBlockItems.map(item => {
        const pricePerKg = blockPriceMapRef.current.get(`${item.product_id}-${tierId}`) || 0
        const formatWeight = parseFloat(item.format_weight_kg) || 0
        const costPerBlock = formatWeight * pricePerKg
        return { ...item, price_per_kg: pricePerKg, cost_per_block: costPerBlock, subtotal: (item.quantity || 0) * costPerBlock, applied_price_tier_id: tierId }
      }))
      return
    }

    // New sale — start with empty list, user adds lines via builder
    setBlockItems([])
  }, [customers])

  /**
   * Initialise or update growing-material items for a customer.
   * Only shows rows if customer is growing_materials_eligible.
   */
  const initGmItemsForCustomer = useCallback((customerId, existingGmItems = null) => {
    if (!customerId) { setGmEligible(false); setGmItems([]); return }

    const customer = customers.find(c => c.id === customerId)
    if (!customer?.growing_materials_eligible) { setGmEligible(false); setGmItems([]); return }

    setGmEligible(true)

    const defaultBranch = customer.branches?.[0]
    const tierId = defaultBranch?.customer_type?.default_price_tier_id

    if (existingGmItems && existingGmItems.length > 0) {
      // Edit mode — re-price existing items with current tier
      setGmItems(existingGmItems.map(item => {
        const unitPrice = gmPriceMapRef.current.get(`${item.growing_material_id}-${tierId}`) || item.unit_price || 0
        return { ...item, unit_price: unitPrice, subtotal: (item.quantity || 0) * unitPrice, applied_price_tier_id: tierId }
      }))
      return
    }

    // New sale — start with empty list, user adds lines via builder
    setGmItems([])
  }, [customers])

  const handleCustomerChange = useCallback((customerId) => {
    setSelectedCustomer(customerId)

    // Reset recurring order state when customer changes
    setIsRecurring(false)
    setRecurringDays([])
    setRecurringEndType('indefinite')
    setRecurringEndDate(null)
    setRecurringOccurrences(null)

    // Load branches from cached customers — synchronous, no network call
    loadCustomerBranches(customerId)

    // Fetch server-side pricing for the new customer
    if (customerId) {
      fetchPricing(customerId)
    }

    // Initialise block items for eligible customers
    initBlockItemsForCustomer(customerId)
    setBlockDraft({ product_id: '', block_format_id: '', quantity: '' })

    // Initialise growing-material items for eligible customers
    initGmItemsForCustomer(customerId)
    setGmDraft({ growing_material_id: '', quantity: '' })
  }, [loadCustomerBranches, fetchPricing, initBlockItemsForCustomer, initGmItemsForCustomer])

  const handleAddNewCustomer = useCallback(() => {
    // Open the customer form modal
    setShowQuickCustomerForm(true)
    // Return a resolved promise (Combobox expects async function)
    return Promise.resolve()
  }, [])

  const handleQuickCustomerSave = async (customerData) => {
    try {
      // Force-refresh customers cache after adding a new customer
      const updatedCustomers = await getCustomers(true)
      setCustomers(updatedCustomers)

      // Find and select the newly created customer
      const newCustomer = updatedCustomers.find(c => c.name === customerData.companyName)
      if (newCustomer) {
        setSelectedCustomer(newCustomer.id)
        loadCustomerBranches(newCustomer.id)
      }

      setShowQuickCustomerForm(false)
    } catch (error) {
      console.error('Error after customer creation:', error)
    }
  }

  /**
   * Handle quantity change — updates quantity synchronously, then triggers debounced server pricing.
   * Server pricing handles all tier/volume-discount/promotion logic.
   */
  const handleQuantityChange = useCallback((lineId, quantity) => {
    const numQuantity = parseFloat(quantity) || 0
    setSaleItems(prev => prev.map(item =>
      item._lineId === lineId
        ? { ...item, quantity: numQuantity, subtotal: numQuantity * (item.price || 0) }
        : item
    ))
    // Debounced server pricing update — quantity change may cross wholesale threshold
    if (selectedCustomer) {
      triggerPricingUpdate(selectedCustomer)
    }
  }, [selectedCustomer, triggerPricingUpdate])

  const handleQuantityInputChange = useCallback((lineId, rawValue) => {
    // Only allow digits and at most one decimal point, up to 3 decimal places during typing
    if (rawValue !== '' && !/^\d*\.?\d{0,3}$/.test(rawValue)) return
    setEditingQuantities(prev => ({ ...prev, [lineId]: rawValue }))
  }, [])

  const handleQuantityInputBlur = useCallback((lineId) => {
    const raw = editingQuantities[lineId]
    setEditingQuantities(prev => {
      const next = { ...prev }
      delete next[lineId]
      return next
    })
    const rounded = roundQuantity(raw)
    handleQuantityChange(lineId, rounded)
  }, [editingQuantities, handleQuantityChange])

  const handleQuantityInputFocus = useCallback((lineId, currentValue) => {
    // Show empty field when value is 0 so user can type directly
    const display = currentValue === 0 ? '' : String(currentValue)
    setEditingQuantities(prev => ({ ...prev, [lineId]: display }))
  }, [])

  /**
   * Handle product type (grade) change — updates product_type_id synchronously,
   * then triggers debounced server pricing to get the correct catalogue price for the new grade.
   */
  const handleProductTypeChange = useCallback((lineId, productTypeId) => {
    setSaleItems(prev => prev.map(item =>
      item._lineId === lineId ? { ...item, product_type_id: productTypeId } : item
    ))
    if (selectedCustomer) {
      triggerPricingUpdate(selectedCustomer)
    }
  }, [selectedCustomer, triggerPricingUpdate])

  // --- Block line-builder handlers ---
  const blockTierId = useMemo(() => {
    if (!selectedCustomer) return null
    const customer = customers.find(c => c.id === selectedCustomer)
    const branch = selectedBranch
      ? customer?.branches?.find(b => b.id === selectedBranch)
      : customer?.branches?.[0]
    return branch?.customer_type?.default_price_tier_id || null
  }, [selectedCustomer, selectedBranch, customers])

  const handleAddBlockLine = useCallback(() => {
    const { product_id, block_format_id, quantity } = blockDraft
    const numQty = parseInt(quantity) || 0
    if (!product_id || !block_format_id || numQty <= 0) return

    const product = products.find(p => p.id === product_id)
    const format = blockFormats.find(f => f.id === block_format_id)
    if (!product || !format) return

    const pricePerKg = blockPriceMapRef.current.get(`${product_id}-${blockTierId}`) || 0
    const weight = parseFloat(format.weight_kg)
    const costPerBlock = weight * pricePerKg

    setBlockItems(prev => [...prev, {
      _lineId: ++blockLineIdRef.current,
      product_id, strain: product.strain,
      block_format_id, format_weight_kg: weight, format_name: format.name,
      quantity: numQty, price_per_kg: pricePerKg,
      cost_per_block: costPerBlock, subtotal: numQty * costPerBlock,
      applied_price_tier_id: blockTierId
    }])
    setBlockDraft({ product_id: '', block_format_id: '', quantity: '' })
    requestAnimationFrame(() => {
      scrollContainerRef.current?.scrollTo({ top: scrollContainerRef.current.scrollHeight, behavior: 'smooth' })
    })
  }, [blockDraft, products, blockFormats, blockTierId])

  const handleDeleteBlockLine = useCallback((lineId) => {
    setBlockItems(prev => prev.filter(item => item._lineId !== lineId))
  }, [])

  const handleEditBlockLine = useCallback((lineId) => {
    setBlockItems(prev => {
      const item = prev.find(i => i._lineId === lineId)
      if (item) {
        setBlockDraft({ product_id: item.product_id, block_format_id: item.block_format_id, quantity: String(item.quantity) })
      }
      return prev.filter(i => i._lineId !== lineId)
    })
  }, [])

  // --- Growing-material line-builder handlers ---
  // Reuses blockTierId — same customer/branch default-tier resolution.
  const handleAddGmLine = useCallback(() => {
    const { growing_material_id, quantity } = gmDraft
    const numQty = parseInt(quantity) || 0
    if (!growing_material_id || numQty <= 0) return

    const material = growingMaterials.find(m => m.id === growing_material_id)
    if (!material) return

    const unitPrice = gmPriceMapRef.current.get(`${growing_material_id}-${blockTierId}`) || 0

    setGmItems(prev => [...prev, {
      _lineId: ++gmLineIdRef.current,
      growing_material_id, name: material.name, unit_label: material.unit_label || 'unit',
      quantity: numQty, unit_price: unitPrice,
      subtotal: numQty * unitPrice,
      applied_price_tier_id: blockTierId
    }])
    setGmDraft({ growing_material_id: '', quantity: '' })
    requestAnimationFrame(() => {
      scrollContainerRef.current?.scrollTo({ top: scrollContainerRef.current.scrollHeight, behavior: 'smooth' })
    })
  }, [gmDraft, growingMaterials, blockTierId])

  const handleDeleteGmLine = useCallback((lineId) => {
    setGmItems(prev => prev.filter(item => item._lineId !== lineId))
  }, [])

  const handleEditGmLine = useCallback((lineId) => {
    setGmItems(prev => {
      const item = prev.find(i => i._lineId === lineId)
      if (item) {
        setGmDraft({ growing_material_id: item.growing_material_id, quantity: String(item.quantity) })
      }
      return prev.filter(i => i._lineId !== lineId)
    })
  }, [])

  // --- Multi-grade line handlers ---
  const handleAddGradeLine = useCallback((lineId) => {
    setSaleItems(prev => {
      const source = prev.find(i => i._lineId === lineId)
      if (!source) return prev

      const usedTypeIds = prev
        .filter(i => i.product_id === source.product_id)
        .map(i => i.product_type_id)

      const availableType = productTypes.find(pt => !usedTypeIds.includes(pt.id))
      if (!availableType) return prev

      const newLine = {
        _lineId: ++saleLineIdRef.current,
        _isPrimary: false,
        id: source.id, product_id: source.product_id,
        productName: source.productName, strain: source.strain,
        product_type_id: availableType.id,
        quantity: 0, catalogue_price: source.catalogue_price || source.price,
        price: source.catalogue_price || source.price, subtotal: 0,
        applied_price_tier_id: source.applied_price_tier_id || null
      }

      // Insert after the last row for this product
      const lastIdx = prev.reduce((last, item, idx) => item.product_id === source.product_id ? idx : last, -1)
      const result = [...prev]
      result.splice(lastIdx + 1, 0, newLine)
      return result
    })
    // Trigger server pricing to get correct price for the new grade line
    if (selectedCustomer) {
      triggerPricingUpdate(selectedCustomer)
    }
  }, [productTypes, selectedCustomer, triggerPricingUpdate])

  const handleRemoveGradeLine = useCallback((lineId) => {
    setSaleItems(prev => prev.filter(item => item._lineId !== lineId))
  }, [])

  const handleSave = () => {
    if (!selectedCustomer) {
      alert('Please select a customer')
      return
    }

    // Only require branch selection if the customer has branches
    if (customerBranches.length > 0 && !selectedBranch) {
      alert('Please select a branch')
      return
    }

    // Filter out items with quantity 0
    const itemsToSave = saleItems.filter(item => item.quantity > 0)
    const blockItemsToSave = blockItems.filter(item => item.quantity > 0)
    const gmItemsToSave = gmItems.filter(item => item.quantity > 0)

    if (itemsToSave.length === 0 && blockItemsToSave.length === 0 && gmItemsToSave.length === 0) {
      alert('Please add at least one item with quantity greater than 0')
      return
    }

    // Validation for recurring orders (US-RECURRING-ORDERS)
    if (isRecurring && recurringDays.length === 0) {
      alert('Please select at least one day for the recurring order')
      return
    }

    if (isRecurring && recurringEndType === 'until_date' && !recurringEndDate) {
      alert('Please select an end date for the recurring order')
      return
    }

    if (isRecurring && recurringEndType === 'after_count' && (!recurringOccurrences || recurringOccurrences < 1)) {
      alert('Please enter a valid number of occurrences (at least 1)')
      return
    }

    // Look up names for the optimistic timeline item
    const customer = customers.find(c => c.id === selectedCustomer)
    const branch = customerBranches.find(b => b.id === selectedBranch)

    // Build sale items data for background persist
    // price = catalogue base price (pre-discount), discount_percent = effective discount (best of volume/promo)
    // DB generated column sub_total = price * quantity * (1 - discount_percent/100)
    const freshItemsData = itemsToSave.map(item => ({
      product_id: item.product_id,
      product_type_id: item.product_type_id,
      quantity: item.quantity,
      price: item.catalogue_price || item.price,
      discount_percent: item.volume_discount_percent ?? null,
      discount_note: item.promotion_name ?? null,
      applied_price_tier_id: item.applied_price_tier_id || null
    }))

    const blockItemsData = blockItemsToSave.map(item => ({
      product_id: item.product_id,
      product_type_id: null,
      block_format_id: item.block_format_id,
      quantity: item.quantity,
      price: item.cost_per_block,
      discount_percent: null,
      discount_note: null,
      applied_price_tier_id: item.applied_price_tier_id || null
    }))

    const gmItemsData = gmItemsToSave.map(item => ({
      product_id: null,
      product_type_id: null,
      block_format_id: null,
      growing_material_id: item.growing_material_id,
      quantity: item.quantity,
      price: item.unit_price,
      discount_percent: null,
      discount_note: null,
      applied_price_tier_id: item.applied_price_tier_id || null
    }))

    const saleItemsData = [...freshItemsData, ...blockItemsData, ...gmItemsData]

    // Build recurring order config if applicable
    let recurring = null
    if (!editingSale && isRecurring && recurringDays.length > 0) {
      const formattedEndDate = recurringEndDate
        ? recurringEndDate instanceof Date
          ? toLocalDateString(recurringEndDate)
          : recurringEndDate
        : null

      recurring = {
        orderConfig: {
          customerId: selectedCustomer,
          branchId: selectedBranch || null,
          daysOfWeek: recurringDays,
          endType: recurringEndType,
          endDate: formattedEndDate,
          maxOccurrences: recurringEndType === 'after_count' ? recurringOccurrences : null
        },
        items: itemsToSave.map(item => ({
          productId: item.product_id,
          productTypeId: item.product_type_id,
          quantity: item.quantity
        }))
      }
    }

    // Optimistic payload — no await, modal closes instantly
    const payload = {
      _isEdit: !!editingSale,
      customer_id: selectedCustomer,
      branch_id: selectedBranch || null,
      customerName: customer?.name || 'Unknown',
      branchName: branch?.name || '',
      date: selectedDate,
      items: [...itemsToSave, ...blockItemsToSave, ...gmItemsToSave],
      total: totalAmount,
      _bgPayload: {
        saleId: editingSale?.id || null,
        saleData: {
          customer_id: selectedCustomer,
          branch_id: selectedBranch || null,
          date: selectedDate
        },
        saleItemsData,
        recurring
      }
    }

    onSave(payload)
    onClose()
  }

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  const handleClearDraft = useCallback(() => {
    const types = productTypes.length ? productTypes : (snapshot.productTypes || [])
    const prods = products.length ? products : (snapshot.products || [])
    const tiers = priceTiers.length ? priceTiers : (snapshot.priceTiers || [])
    const defaultProductType = types.find(pt => pt.name === 'A Class') || types[0]
    const retailTier = tiers.find(t => t.name === 'retail')

    setSelectedCustomer('')
    setSelectedBranch('')
    setCustomerBranches([])
    setSelectedDate(getTodayISOString())
    setConsignmentNo('')
    setBlockEligible(false)
    setBlockItems([])
    setBlockDraft({ product_id: '', block_format_id: '', quantity: '' })
    blockLineIdRef.current = 0
    setGmEligible(false)
    setGmItems([])
    setGmDraft({ growing_material_id: '', quantity: '' })
    gmLineIdRef.current = 0
    saleLineIdRef.current = 0
    setSaleItems(prods.map(product => ({
      _lineId: ++saleLineIdRef.current,
      _isPrimary: true,
      id: product.id, product_id: product.id,
      productName: product.strain, strain: product.strain,
      product_type_id: defaultProductType?.id || null,
      quantity: 0, catalogue_price: product.base_price_per_kg || 0,
      price: product.base_price_per_kg || 0, subtotal: 0,
      applied_price_tier_id: retailTier?.id || null
    })))
    setIsRecurring(false)
    setRecurringDays([])
    setRecurringEndType('indefinite')
    setRecurringEndDate(null)
    setRecurringOccurrences(null)

    if (onClearFormData) onClearFormData()
  }, [productTypes, products, priceTiers, snapshot, onClearFormData])

  // ESC key handler
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey)
      return () => document.removeEventListener('keydown', handleEscKey)
    }
  }, [isOpen, handleClose])

  // Handle delete flow - open AlertDialog after Dialog closes
  useEffect(() => {
    if (pendingDelete && !isOpen) {
      // Small delay to ensure Dialog is fully closed
      const timer = setTimeout(() => {
        setShowDeleteConfirm(true)
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [pendingDelete, isOpen])

  const totalAmount = useMemo(() => {
    const freshTotal = saleItems.reduce((sum, item) => sum + item.subtotal, 0)
    const blockTotal = blockItems.reduce((sum, item) => sum + item.subtotal, 0)
    const gmTotal = gmItems.reduce((sum, item) => sum + item.subtotal, 0)
    return freshTotal + blockTotal + gmTotal
  }, [saleItems, blockItems, gmItems])

  const customerOptions = useMemo(() =>
    customers.map(c => ({
      value: c.id,
      label: `${c.name}${c.branches?.[0]?.customer_types?.name ? ` (${c.branches[0].customer_types.name})` : ''}`
    })),
    [customers]
  )

  const handleDelete = () => {
    // Close the Dialog first to avoid focus trap conflict
    setPendingDelete(true)
    onClose()
  }

  const handleDeleteConfirm = () => {
    if (onDelete && editingSale?.id) {
      onDelete(editingSale.id)
      setShowDeleteConfirm(false)
      setPendingDelete(false)
    }
  }

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false)
    setPendingDelete(false)
    // Request parent to reopen the edit modal
    if (editingSale && onRequestReopen) {
      // Small delay to ensure smooth transition
      setTimeout(() => {
        onRequestReopen()
      }, 100)
    }
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}>
        <DialogContent className="odin-modal-large flex flex-col">
        <DialogHeader className="pb-2 flex-shrink-0">
          <DialogTitle className="text-2xl font-bold">{editingSale ? 'Edit Sale' : 'Create Sale'}</DialogTitle>
        </DialogHeader>

        <div ref={scrollContainerRef} className="flex flex-col space-y-4 flex-grow overflow-auto pl-[15px]">
          <div className="grid grid-cols-4 gap-4 flex-shrink-0">
            <div className="space-y-2 w-full min-w-0">
              <Label htmlFor="customer-select">Customer</Label>
              <Combobox
                value={selectedCustomer}
                onChange={handleCustomerChange}
                options={customerOptions}
                placeholder="Select Customer"
                searchPlaceholder="Search customers..."
                emptyMessage="No customers found."
                disabled={loading}
                allowAddNew={true}
                onAddNew={handleAddNewCustomer}
                addNewLabel="Add New Customer"
                className="ml-[5px]"
              />
            </div>

            <div className="space-y-2 w-full min-w-0">
              <Label htmlFor="branch-select">Branch</Label>
              <Select 
                value={selectedBranch} 
                onValueChange={setSelectedBranch} 
                disabled={!selectedCustomer || customerBranches.length === 0 || loading}
              >
                <SelectTrigger className="w-full">
                  <SelectValue 
                    placeholder={
                      !selectedCustomer 
                        ? 'Select customer first' 
                        : customerBranches.length === 0 
                          ? 'No branches available' 
                          : 'Select Branch'
                    } 
                  />
                </SelectTrigger>
                <SelectContent>
                  {customerBranches.map(branch => (
                    <SelectItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 w-full min-w-0">
              <Label htmlFor="date-picker">
                Date ({formatDate(selectedDate)})
              </Label>
              <DatePicker
                className="w-full"
                date={typeof selectedDate === 'string' ? parseDateString(selectedDate) : selectedDate}
                onDateChange={(d) => setSelectedDate(d instanceof Date ? toLocalDateString(d) : d)}
                placeholder="Select date"
              />
            </div>

            <div className="space-y-2 w-full min-w-0">
              {/* Empty column - keeps grid balanced */}
            </div>
          </div>

          {/* Recurring Order Checkbox - only for new sales */}
          {!editingSale && (
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="recurring"
                  checked={isRecurring}
                  onCheckedChange={setIsRecurring}
                  disabled={!selectedCustomer}
                />
                <Label
                  htmlFor="recurring"
                  className={cn(
                    "cursor-pointer",
                    !selectedCustomer && "text-muted-foreground cursor-not-allowed"
                  )}
                >
                  Make this a recurring order
                </Label>
              </div>

              {isRecurring && (
                <RecurringOrderOptions
                  selectedDays={recurringDays}
                  onDaysChange={setRecurringDays}
                  endType={recurringEndType}
                  onEndTypeChange={setRecurringEndType}
                  endDate={recurringEndDate}
                  onEndDateChange={setRecurringEndDate}
                  occurrences={recurringOccurrences}
                  onOccurrencesChange={setRecurringOccurrences}
                />
              )}
            </div>
          )}

          {/* Sale Items Table */}
          <div>
            <div className="border rounded-md">
              <div className="min-w-[700px]">
                <Table>
                <TableHeader className="sticky top-0 bg-white z-10">
                  <TableRow>
                    <TableHead className="w-[200px]">Product</TableHead>
                    <TableHead className="w-[160px]">Product Type</TableHead>
                    <TableHead className="w-[100px]">Qty (kg)</TableHead>
                    <TableHead className="w-[110px]">Price (£/kg)</TableHead>
                    <TableHead className="w-[120px]">Subtotal (£)</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {saleItems.map((item) => {
                    // Count how many grades are already used for this product
                    const usedGradeCount = saleItems.filter(i => i.product_id === item.product_id).length
                    const canAddGrade = usedGradeCount < productTypes.length

                    return (
                    <TableRow key={item._lineId} className={cn(
                      item.quantity > 0 ? 'bg-green-50/50 dark:bg-green-950/5' : 'opacity-70'
                    )}>
                      <TableCell className="font-medium">
                        {item._isPrimary ? item.productName : (
                          <span className="text-muted-foreground pl-4">↳ {item.productName}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          // Grades used by OTHER rows for the same product (not this row)
                          const usedByOthers = saleItems
                            .filter(i => i.product_id === item.product_id && i._lineId !== item._lineId)
                            .map(i => i.product_type_id)
                          return (
                          <Select
                            value={item.product_type_id}
                            onValueChange={(value) => handleProductTypeChange(item._lineId, value)}
                          >
                            <SelectTrigger className="w-full" tabIndex={-1}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {productTypes.map(type => (
                                <SelectItem key={type.id} value={type.id} disabled={usedByOthers.includes(type.id)}>
                                  {type.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          )
                        })()}
                      </TableCell>
                      <TableCell>
                        <Input
                          type="text"
                          inputMode="decimal"
                          value={editingQuantities[item._lineId] !== undefined ? editingQuantities[item._lineId] : (item.quantity || '')}
                          onChange={(e) => handleQuantityInputChange(item._lineId, e.target.value)}
                          onFocus={() => handleQuantityInputFocus(item._lineId, item.quantity)}
                          onBlur={() => handleQuantityInputBlur(item._lineId)}
                          placeholder="0.0"
                          className="w-full max-w-[80px]"
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          £{item.price.toFixed(2)}
                          {item.promotion_name && (
                            <div className="text-xs text-purple-600 mt-0.5">{item.promotion_name}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        £{item.subtotal.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {item._isPrimary && canAddGrade ? (
                          <button
                            type="button"
                            onClick={() => handleAddGradeLine(item._lineId)}
                            className="p-1 text-gray-400 hover:text-gray-700"
                            title="Add another grade"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        ) : !item._isPrimary ? (
                          <button
                            type="button"
                            onClick={() => handleRemoveGradeLine(item._lineId)}
                            className="p-1 text-gray-400 hover:text-red-600"
                            title="Remove grade"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                    )
                  })}
                </TableBody>
                </Table>
                </div>
            </div>
          </div>

          {/* Fruiting Blocks — line builder. Hidden: substrate blocks now live
              under Growing Materials. Historical block sales still render in
              invoice views; this builder is retired for new sales. */}
          {SHOW_FRUITING_BLOCKS_BUILDER && blockEligible && (
            <div className="mt-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Fruiting Blocks</h4>

              {/* Input row — progressive disclosure */}
              <div className="flex items-end gap-2 mb-2">
                <div className="w-[160px]">
                  <Label className="text-xs text-muted-foreground mb-1">Strain</Label>
                  <Select
                    value={blockDraft.product_id}
                    onValueChange={(value) => setBlockDraft(prev => ({ ...prev, product_id: value, block_format_id: '', quantity: '' }))}
                  >
                    <SelectTrigger className="w-full h-9">
                      <SelectValue placeholder="Select strain" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.filter(p => p.active !== false).map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.strain}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {blockDraft.product_id && (
                  <div className="w-[180px]">
                    <Label className="text-xs text-muted-foreground mb-1">Format</Label>
                    <Select
                      value={blockDraft.block_format_id}
                      onValueChange={(value) => setBlockDraft(prev => ({ ...prev, block_format_id: value, quantity: '' }))}
                    >
                      <SelectTrigger className="w-full h-9">
                        <SelectValue placeholder="Select format" />
                      </SelectTrigger>
                      <SelectContent>
                        {blockFormats.map(f => (
                          <SelectItem key={f.id} value={f.id}>
                            {f.name} ({parseFloat(f.weight_kg).toFixed(1)} kg)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {blockDraft.block_format_id && (
                  <div className="w-[80px]">
                    <Label className="text-xs text-muted-foreground mb-1">Qty</Label>
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      value={blockDraft.quantity}
                      onChange={(e) => setBlockDraft(prev => ({ ...prev, quantity: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddBlockLine() } }}
                      placeholder="0"
                      className="h-9"
                    />
                  </div>
                )}

                {parseInt(blockDraft.quantity) > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 px-3"
                    onClick={handleAddBlockLine}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {/* Added lines */}
              {blockItems.length > 0 && (
                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Strain</TableHead>
                        <TableHead>Format</TableHead>
                        <TableHead className="w-[70px]">Qty</TableHead>
                        <TableHead className="w-[100px]">Cost/Block</TableHead>
                        <TableHead className="w-[100px]">Subtotal</TableHead>
                        <TableHead className="w-[80px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {blockItems.map((item) => (
                        <TableRow key={item._lineId} className="bg-amber-50/50">
                          <TableCell className="font-medium">{item.strain}</TableCell>
                          <TableCell>{item.format_name} ({item.format_weight_kg.toFixed(1)} kg)</TableCell>
                          <TableCell>{item.quantity}</TableCell>
                          <TableCell>£{(item.cost_per_block || 0).toFixed(2)}</TableCell>
                          <TableCell>£{(item.subtotal || 0).toFixed(2)}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <button onClick={() => handleEditBlockLine(item._lineId)} className="p-1 text-gray-500 hover:text-gray-800" title="Edit">
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button onClick={() => handleDeleteBlockLine(item._lineId)} className="p-1 text-gray-500 hover:text-red-600" title="Delete">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {blockItems.length === 0 && !blockDraft.product_id && (
                <p className="text-xs text-muted-foreground">Select a strain to add fruiting blocks to this order.</p>
              )}
            </div>
          )}

          {/* Growing Materials — line builder for eligible customers */}
          {gmEligible && (
            <div className="mt-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Growing Materials</h4>

              {/* Input row — progressive disclosure */}
              <div className="flex items-end gap-2 mb-2">
                <div className="w-[260px]">
                  <Label className="text-xs text-muted-foreground mb-1">Material</Label>
                  <Select
                    value={gmDraft.growing_material_id}
                    onValueChange={(value) => setGmDraft(prev => ({ ...prev, growing_material_id: value, quantity: '' }))}
                  >
                    <SelectTrigger className="w-full h-9">
                      <SelectValue placeholder="Select material" />
                    </SelectTrigger>
                    <SelectContent>
                      {growingMaterials.length === 0 ? (
                        <div className="px-2 py-1.5">
                          <p className="text-sm italic text-muted-foreground">No growing materials added</p>
                          <button
                            type="button"
                            onClick={goToGrowingMaterialsCatalogue}
                            className="mt-1 flex items-center gap-1 text-sm font-medium text-[#009689] hover:underline"
                          >
                            <Plus className="h-3.5 w-3.5" />
                            Add growing materials
                          </button>
                        </div>
                      ) : (
                        growingMaterials.map(m => (
                          <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {gmDraft.growing_material_id && (
                  <div className="w-[80px]">
                    <Label className="text-xs text-muted-foreground mb-1">Qty</Label>
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      value={gmDraft.quantity}
                      onChange={(e) => setGmDraft(prev => ({ ...prev, quantity: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddGmLine() } }}
                      placeholder="0"
                      className="h-9"
                    />
                  </div>
                )}

                {parseInt(gmDraft.quantity) > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 px-3"
                    onClick={handleAddGmLine}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {/* Added lines */}
              {gmItems.length > 0 && (
                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Material</TableHead>
                        <TableHead className="w-[70px]">Qty</TableHead>
                        <TableHead className="w-[110px]">Unit Price</TableHead>
                        <TableHead className="w-[100px]">Subtotal</TableHead>
                        <TableHead className="w-[80px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {gmItems.map((item) => (
                        <TableRow key={item._lineId} className="bg-emerald-50/50">
                          <TableCell className="font-medium">{item.name}</TableCell>
                          <TableCell>{item.quantity} {item.unit_label}</TableCell>
                          <TableCell>£{(item.unit_price || 0).toFixed(2)}</TableCell>
                          <TableCell>£{(item.subtotal || 0).toFixed(2)}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <button onClick={() => handleEditGmLine(item._lineId)} className="p-1 text-gray-500 hover:text-gray-800" title="Edit">
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button onClick={() => handleDeleteGmLine(item._lineId)} className="p-1 text-gray-500 hover:text-red-600" title="Delete">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {gmItems.length === 0 && !gmDraft.growing_material_id && (
                <p className="text-xs text-muted-foreground">Select a material to add growing materials to this order.</p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="pt-4 flex-shrink-0 flex items-center justify-between">
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            {!editingSale && selectedCustomer && (
              <Button variant="ghost" className="text-destructive hover:text-destructive" onClick={handleClearDraft} disabled={loading}>
                Clear
              </Button>
            )}
            {editingSale?.id && onDelete && (
              <Button variant="destructive" onClick={handleDelete} disabled={loading}>
                Delete
              </Button>
            )}
            <Button
              variant={editingSale ? "default" : "create"}
              onClick={handleSave}
              disabled={loading || pricingLoading}
            >
              {loading ? 'Saving...' : editingSale ? 'Update Sale' : 'Create Sale'}
            </Button>
          </div>
          <div className="text-right">
            <span className="text-lg font-bold">Total: £{totalAmount.toFixed(2)}</span>
          </div>
        </DialogFooter>

        {/* Quick Customer Form Modal */}
        {showQuickCustomerForm && (
          <CustomerForm
            isOpen={showQuickCustomerForm}
            onClose={() => setShowQuickCustomerForm(false)}
            onSave={handleQuickCustomerSave}
            editingCustomer={null}
            savedFormData={null}
            onFormDataChange={null}
          />
        )}
      </DialogContent>
    </Dialog>

    {/* Delete Confirmation Dialog - Rendered outside of main Dialog */}
    {!isOpen && (
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Sale</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this sale? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDeleteCancel}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} variant="destructive">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    )}
    </>
  )
}
